import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { normalizePermissions } from '../auth/auth-permissions.util';
import { ApiEndpoints } from '../api/api-endpoints';
import { AccessProfile } from '../models/auth.model';

/**
 * Parses the `GET /me/permissions` response into an `AccessProfile`.
 *
 * The backend documents the contract as three camelCase keys, where `roles`
 * and `permissions` are string arrays that are never null and `userType` is
 * one of Student, Professor, Staff or SuperUser. We read exactly that shape.
 *
 * What this parser still guarantees is that it never throws and never yields
 * anything but an `AccessProfile`: the response is not typed in the API's
 * OpenAPI document, so nothing mechanically enforces the contract. A payload
 * that does not match degrades to an empty profile, which both the route
 * guard and the sidebar handle by falling back to role names.
 */
export const parseAccessProfile = (raw: unknown): AccessProfile => {
  if (!raw || typeof raw !== 'object') {
    return { userType: null, roles: [], permissions: [] };
  }

  const source = raw as Record<string, unknown>;
  const userType = source['userType'];

  return {
    userType: typeof userType === 'string' && userType ? userType : null,
    // Role casing is preserved: these are area names shown to the user.
    roles: toStringArray(source['roles']),
    // Permissions are compared case-insensitively, so they are lowercased.
    permissions: normalizePermissions(toStringArray(source['permissions'])),
  };
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0),
    ),
  );
};

@Injectable({ providedIn: 'root' })
export class AccessControlService {
  private readonly http = inject(HttpClient);

  getAccessProfile(): Observable<AccessProfile> {
    return this.http
      .get<unknown>(ApiEndpoints.mePermissions)
      .pipe(map((raw) => parseAccessProfile(raw)));
  }
}
